<template>
    <div class="flex flex-col h-full gap-2 grid-border">
        <div class="flex flex-col h-0 grow">
            <dx-data-grid
                ref="dxGridRef"
                :allow-column-reordering="true"
                :allow-column-resizing="true"
                :column-auto-width="true"
                :column-chooser="{ enabled: true, mode: 'select' }"
                :column-min-width="columnsMinWidth"
                :column-resizing-mode="'nextColumn'"
                :columns="meta?.columns"
                :data-source="displayList"
                :export="{ enabled: true, allowExportSelectedData: true, formats: exportFormats }"
                :filter-panel="{ visible: meta.isFilterPanelVisible ?? true }"
                :filter-row="{ visible: meta.isFilterRowVisible ?? true }"
                :fixing="{ enabled: true }"
                :focused-column-index="0"
                :focused-row-enabled="true"
                :header-filter="{ visible: meta.headerFilterVisible }"
                :hover-state-enabled="true"
                :load-panel="{ enabled: true }"
                :paging="{ enabled: meta?.isPagingEnabled || false }"
                :scrolling="{ mode: meta?.scrollingMode || 'virtual' }"
                :search-panel="{
                    visible: true,
                    placeholder: 'Фильтр...',
                    locateInMenu: 'auto',
                    applyFilter: 'onEnter',
                }"
                :selection="{
                    mode: selectionMode,
                    showCheckBoxesMode: meta?.showCheckBoxesMode || 'always',
                    allowSelectAll: meta?.allowSelectAll || false,
                }"
                :show-borders="true"
                :show-column-lines="true"
                :show-row-lines="true"
                :sorting="{ mode: 'single' }"
                :state-storing="{ enabled: true, type: 'custom', customSave: saveState, savingTimeout: 500 }"
                :toolbar="{ items: toolbarItems }"
                :word-wrap-enabled="meta?.wordWrapEnabled ?? true"
                class="h-0 grow customSelector"
                height="100%"
                key-expr="greyEminence"
                @exporting="onExporting"
                @focusedCellChanging="onFocusedCellChanging"
                @saved="onSaved"
                @row-click="onRowClick"
                @selection-changed="selectionChanged"
                @focused-row-changed="onFocusedRowChanged"
                @cell-click="onCellClick"
                @option-changed="onOptionChanged"
                @context-menu-preparing="onContextMenuPreparing"
                @editor-prepared="onEditorPrepared"
                @editor-preparing="onEditorPreparing"
                @editing-start="onEditingStart"
                @content-ready="onContentReady"
            >
                <dx-toolbar :height="'64px'" :visible="toolbarShow">
                    <dx-item
                        v-for="(item, index) in toolbarConfigAfter"
                        :key="index"
                        :name="item.component.name"
                        location="after"
                    >
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            :disabled="refs[item.component.disabledRefName]"
                            :hint="item.component.hint"
                            class="ai-button-base"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                        <dx-text-box
                            v-if="item.widget === 'dx-text-box'"
                            v-model:value="refs[item.component.valueRefName.name]"
                            :label="
                                refs[item.component.valueLabelName]
                                    ? refs[item.component.valueLabelName]
                                    : item.component.valueLabelName
                            "
                            :width="item.component.width"
                            class="ai-toolbar-texteditor"
                            @enter-key="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'enter-key';
                                    eventController(event);
                                }
                            "
                            @key-up="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'key-up';
                                    eventController(event);
                                }
                            "
                        >
                            <DxTextBoxButton
                                v-for="(inputButton, indexButton) in item.component.inputButtons"
                                :key="indexButton"
                                :name="inputButton.name"
                                :options="inputButton.options"
                                location="after"
                            />
                        </dx-text-box>
                        <DxDropDownButton
                            v-if="item.widget === 'dx-drop-down-button'"
                            :disabled="refs[item.component.disabledRefName]"
                            :dropDownOptions="item.component.dropDownOptions"
                            :hint="item.hint"
                            :icon="item.component.htmlElement"
                            :items="item.component.items"
                            :split-button="true"
                        >
                            <dx-item v-for="(item, index) in item.component.items" :key="index">
                                <div
                                    :class="{
                                        'opacity-25': refs[item.component.disabledRefName],
                                        'ai-dx-drop-down-button-item dx-template-wrapper dx-item-content dx-list-item-content': true,
                                    }"
                                    @click="
                                        event => {
                                            event['item'] = item;
                                            event['eventName'] = 'click';
                                            eventController(event);
                                        }
                                    "
                                >
                                    <component
                                        :is="item.component.htmlElement"
                                        :disabled="refs[item.component.disabledRefName]"
                                        :isChecked="vIsCurrentSettingsUser"
                                        :name="item.component.name"
                                        class="ai-dx-drop-down-button-item-svg"
                                    />
                                    <div>
                                        {{
                                            typeof item.component.text === 'function'
                                                ? item.component.text()
                                                : item.component.text
                                        }}
                                    </div>
                                </div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>
                    <dx-item :visible="!!meta.title" location="before" name="titleGrid">
                        <div class="flex gap-2">
                            <h3>{{ `${meta.title}` }}</h3>
                        </div>
                    </dx-item>
                    <dx-item v-if="meta?.customElement" location="before" template="customElement" />
                    <dx-item :visible="groupPanelVisible" location="before" name="groupPanel" />
                </dx-toolbar>
                <dx-column
                    v-for="(item, index) in processedColumns"
                    :key="index"
                    :alignment="item.alignment"
                    :allow-editing="item.allowEditing ?? true"
                    :calculate-cell-value="calculateCellValue"
                    :calculate-display-value="item.calculateDisplayValue"
                    :calculate-filter-expression="item.calculateFilterExpression || undefined"
                    :caption="item.caption"
                    :cell-template="item.cellTemplate"
                    :component-meta="item.component"
                    :data-field="item.dataField"
                    :data-type="item.dataType || 'string'"
                    :edit-cell-template="chooseEditor(item)"
                    :editor-meta="item.editor"
                    :editor-options="{
                        ...item.editorOptions,
                        maxLength: item.maxLength,
                        showClearButton: isDate(item),
                    }"
                    :fixed="item.fixed"
                    :format="item.format"
                    :group-index="item.groupIndex"
                    :lookup="item.lookup || null"
                    :show-in-column-chooser="item.showInColumnChooser || true"
                    :sort-index="item.sortIndex"
                    :sort-order="item.sortOrder"
                    :validation-rules="item.validationRules"
                    :visible="item.visible"
                    :width="item.width"
                    headerCellTemplate="headerTemplate"
                >
                    <DxLookup
                        v-if="item.editor?.type === 'ai-drop-down' || item.editor?.type === 'ai-popup'"
                        v-model:data-source="lookupList[item.dataField]"
                        :display-expr="item.editor?.text"
                        :value-expr="item.dataField"
                    />
                </dx-column>
                <dx-editing
                    :allow-adding="false"
                    :allow-deleting="false"
                    :allow-updating="editing?.allowUpdating"
                    mode="batch"
                    start-edit-action="click"
                />
                <dx-master-detail :enabled="meta?.enableMasterDetail" template="detail" />

                <template #preferences>
                    <dx-check-box
                        :value="checkboxValue"
                        text="Автоматически сохранять настройки"
                        @value-changed="preferencesCheckChanged"
                    />
                </template>

                <template #dropDownBoxEditor="{ data: cellInfo }">
                    <ai-drop-down
                        :cellInfo="cellInfo"
                        :data-source="lookupList[cellInfo.column.dataField]"
                        :display-expr="cellInfo.column.editorMeta?.text"
                        :on-value-changed="cellInfo.setValue"
                        :value="cellInfo.value"
                        :value-expr="cellInfo.column.dataField"
                        @select="onLookUpSelect"
                        @end-editing="onEditingEnd"
                    />
                </template>
                <template #popupEditor="{ data: cellInfo }">
                    <ai-popup
                        :cell-info="cellInfo"
                        :display-value="cellInfo.displayValue"
                        :on-value-changed="cellInfo.setValue"
                        :onDotsClick="dotsClick"
                        @select="onLookUpSelect"
                        @end-editing="onEditingEnd"
                        @on-field-clearing="onFieldClearing"
                    />
                </template>
                <template #headerTemplate="{ data: headerInfo }">
                    <div class="h-full flex items-start text-left line-clamp-3">
                        {{ headerInfo.column.caption }}
                    </div>
                </template>
                <template v-for="(_, name) in $slots" #[name]="{ data }">
                    <slot :data="data" :name="name" locate-in-menu="auto"></slot>
                </template>
            </dx-data-grid>
        </div>
        <grid-footer v-if="showGridFooter" ref="childRef" />
    </div>
</template>

<script>
export default {
    name: 'ai-grid-edit-core',
};

/* Чтобы ограничить количество символов, которые можно добавить в поле ввода, необходимо в мету колонки прописать настройку 'maxLength: 250' (например, 250 символов)*/

/*
Чтобы слова в заголовках столбцов НЕ переносились необходимо добавить в мету этой таблицы настройку 'wordWrapEnabled: false,'
*/
</script>
<script setup>
import { computed, onBeforeMount, onMounted, reactive, ref, onUnmounted } from 'vue';
import { DxDataGrid, DxEditing, DxColumn, DxLookup, DxToolbar, DxItem, DxMasterDetail } from 'devextreme-vue/data-grid';
import { DxTextBox, DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { DxCheckBox } from 'devextreme-vue/check-box';
import gridFooter from '../grid-footer.vue';
import onExporting from '@/utils/export.js';
import AiDropDown from '@/components/ai-drop-down.vue';
import AiPopup from '@/components/ai-popup.vue';
import deepEqual from '@/utils/deepEqual.js';
import { useRouter } from 'vue-router';
// import { getUserDataChanges } from '@/service/userdata-service';

// const timeoutId = ref(null);
const columnsMinWidth = ref(70);
const childRef = ref();

const dxGridRef = ref();
const gridInstance = ref();
const toolbarItems = ref([]);
const checkboxValue = computed(() => props.preferencesState);
const exportFormats = computed(() => (props.meta?.exportFormats ? props.meta.exportFormats : ['xlsx', 'pdf']));
const userData = ref({ selectedRowKeys: [], gridChanges: [], lookupData: [] });
const userSettingsData = ref({});
const selectionMode = ref('multiple');
const focusedFilterColumnIndex = ref(null);
const toolbarState = reactive({
    selectButtonState: { instance: null, disabled: true },
    copyButtonState: { instance: null, disabled: true },
    deleteButtonState: { instance: null, disabled: true },
    preferencesButtonState: { instance: null, items: null },
    saveChangesButtonState: { instance: null, disabled: true },
});
const visibleColumns = computed(() => props.meta.columns.map(col => col.dataField));
const booleanColumns = computed(() =>
    props.meta.columns.filter(col => col.dataType === 'boolean').map(col => col.dataField)
);
const processedColumns = computed(() => {
    if (props.meta.sort) {
        const { type, field } = props.meta.sort;

        return props.meta.columns.map(col => {
            if (col.dataField === field) col.sortOrder = type;
            return col;
        });
    }

    return props.meta.columns.map(col => col);
});
const vEditViewRoutName = computed(() => {
    if (props.editViewRoutName !== undefined) {
        return props.editViewRoutName;
    }
    if (props.meta?.editViewRoutName !== undefined) {
        return props.meta?.editViewRoutName;
    }
    return undefined;
});

const refs = ref({
    isChoiceButtonDisabled: true,
    isSaveButtonDisabled: true,
    isDeleteButtonDisabled: true,
    isCopyButtonDisabled: true,
    isEditButtonDisabled: true,
    isSaveSettingsButtonDisabled: true,
});
const groupPanelVisible = ref(false);
const vIsCurrentSettingsUser = ref(true);
const router = useRouter();
const getRefValue = name => {
    return refs.value[name];
};
const setRefValue = (name, value) => {
    refs.value[name] = value;
};
let toolbarConfigAfter = [];
let toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'choice',
            disabledRefName: 'isChoiceButtonDisabled',
            hint: 'Выбрать и закрыть',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-choice-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'copy',
            disabledRefName: 'isCopyButtonDisabled',
            hint: 'Создать копию сфокусированной строки',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-copy-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'edit',
            disabledRefName: 'isEditButtonDisabled',
            hint: 'Редактировать',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-edit-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'add',
            disabledRefName: 'isAddButtonDisabled',
            hint: 'Добавить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'delete',
            disabledRefName: 'isDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'save',
            disabledRefName: 'isSaveButtonDisabled',
            hint: 'Сохранить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-save-base',
        },
    },
    {
        widget: 'searchPanel',
        component: {
            name: 'searchPanel',
        },
    },
    {
        widget: 'dx-drop-down-button',
        hint: 'Экспортировать',
        component: {
            name: 'export',
            htmlElement: `<svg width="32" height="32" viewBox="0 0 76 74" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_1695_4138)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C2.23858 0 0 2.23858 0 5V69C0 71.7614 2.23858 74 5 74H57C59.7614 74 62 71.7614 62 69V21H44C42.3431 21 41 19.6569 41 18V0H5Z" fill="#286EB6"/>
<path d="M45 1L53 9L61 17H48C46.3431 17 45 15.6569 45 14V1Z" fill="#286EB6"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879ZM38.2068 51.2919L52.8038 51.2879ZM52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" fill="white"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879M52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" stroke="#286EB6" stroke-width="4"/>
<path d="M32 41C32 39.8954 32.8954 39 34 39H55V49H34C32.8954 49 32 48.1046 32 47V41Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_1695_4138">
<rect width="76" height="74" fill="white"/>
</clipPath>
</defs>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-all-base',
                        name: 'export-pdf-all',
                        text: 'Экспортировать все в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-selected-base',
                        name: 'export-pdf-selected',
                        text: 'Экспортировать выбранные строки в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-all-base',
                        name: 'export-excel-all',
                        text: 'Экспортировать всё в EXCEL',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-selected-base',
                        name: 'export-excel-selected',
                        text: 'Экспортировать выбранные строки в EXCEL',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-drop-down-button',
        hint: 'Настройки',
        component: {
            name: 'settings',
            htmlElement: `<svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z" fill="#286EB6"/>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'group-area',
                        text: () => {
                            return !groupPanelVisible.value
                                ? 'Показать область группировки'
                                : `${String.fromCharCode(10004)}  Показать область группировки`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-auto-settings-save',
                        text: 'Автоматически сохранять настройки',
                        isChecked: vIsCurrentSettingsUser.value,
                        htmlElement: 'ai-checkbox',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-user-settings',
                        text: 'Сохранить настройки',
                        disabledRefName: 'isSaveSettingsButtonDisabled',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'clear-user-settings',
                        text: 'Сбросить настройки',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'show-column-chooser',
            hint: 'Выбор столбцов',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-show-column-chooser-base',
        },
    },
];
const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    toolbarShow: {
        type: Boolean,
        default: () => true,
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    showGridFooter: {
        type: Boolean,
        default: () => true,
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['choice', 'edit'],
    },
    toolbarConfig: {
        type: Object,
        default: () => {},
    },
    editing: {
        type: Object,
        default: () => {},
    },
    dataSource: {
        type: Array,
        default: () => [],
    },
    lockedRows: {
        type: Array,
        default: () => [],
    },
    lookupsData: {
        type: Object,
        default: () => {},
    },
    preferencesState: {
        type: Boolean,
    },
    initialFocusedRowIndex: {
        type: Number,
        default: 0,
    },
    editViewRoutName: {
        type: String,
        default: () => {
            return undefined;
        },
    },
    addCustomClick: {
        type: Boolean,
        default: false,
    },
    dotsClick: {
        type: Function,
        default: metaCell => {
            console.log('ai-grid-edit-core default dotsClick(metaCell) metaCell: ' + JSON.stringify({}));
            return {};
        },
    },
});

onBeforeMount(() => {
    // toolbar
    for (const item of toolbarConfigInitAfter) {
        if (!props.toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }
    }
    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };
    const toolbarUserItems = [...props.toolbarUserItems].sort(compare);

    for (const item of toolbarUserItems) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }
});

onMounted(() => {
    gridInstance.value = dxGridRef.value.instance;
    const visibleColumns = gridInstance.value.getVisibleColumns();

    if (visibleColumns && visibleColumns.length > 0) {
        columnsMinWidth.value = `${100 / visibleColumns.length}%`;
    }

    setSelectionMode();
    emit('coreMounted');
});

const onContentReady = e => {
    childRef.value?.setTotalCount(gridInstance.value.getVisibleRows().length);
};

const eventController = async e => {
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
            e = null;
        }
    } else if (e.item.widget === 'dx-text-box') {
        if (e.eventName === 'enter-key' || e.eventName === 'key-up') {
            await toolbarTextboxEvent(e);
            e = null;
        }
    } else if (e.item.widget === 'dx-drop-down-item') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
            e = null;
        }
    }
};

const isDate = (column) => column.dataType === 'date' || column.dataType === 'datetime';

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'refresh':
            await onRefreshClick(e);
            break;
        case 'edit':
            await onEditClick(e);
            break;
        case 'copy':
            await onCopyClick(e);
            break;
        case 'add':
            await onAddClick(e);
            break;
        case 'delete':
            await onDeleteClick(e);
            break;
        case 'save':
            await onSaveClick(e);
            break;
        case 'export-excel-all':
            onExcelExportAll(e);
            break;
        case 'export-excel-selected':
            onExcelExportSelected(e);
            break;
        case 'export-pdf-all':
            onPdfExportAll(e);
            break;
        case 'export-pdf-selected':
            onPdfExportSelected(e);
            break;
        case 'group-area':
            onGroupAreaClick(e);
            break;
        case 'apply-auto-settings-save':
            applyAutoSettingsSave(e);
            break;
        case 'apply-user-settings':
            applyAutoSettings(e);
            break;
        case 'clear-user-settings':
            clearUserSettings(e);
            break;
        case 'show-column-chooser':
            dxGridRef.value?.instance.showColumnChooser();
            break;
        default:
            break;
    }
    emit('toolbarButtonClick', e);
};
const toolbarTextboxEvent = async e => {
    switch (e.eventName) {
        case 'enter-key':
            switch (e.item.component.name) {
                case 'find':
                    await onEnterKeyFilter(e);
                    break;
                default:
                    break;
            }
            break;
        case 'key-up':
            switch (e.item.component.name) {
                case 'find':
                    await onFilterKeyup(e);
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
    emit('toolbarTextboxEvent', e);
};
const onEnterKeyFilter = e => {
    // console.log('onEnterKeyFilter($event): ', e);
};
const onFilterKeyup = async e => {
    // console.log('onFilter1Keyup($event): ', e.event.currentTarget.value);
};

const onEditClick = () => {
    const id = gridInstance.value.option('focusedRowKey');
    if (id && vEditViewRoutName.value) {
        router.push({
            name: vEditViewRoutName.value,
            params: { id },
        });
    }
};

function onExcelExportAll(e) {
    // console.log('onExcelExportAll', e);
}

function onExcelExportSelected(e) {
    // console.log('onExcelExportSelected', e);
}

function onPdfExportAll(e) {
    // console.log('onPdfExportAll', e);
}

const onFieldClearing = cell => {
    cell.text = '';
    gridInstance.value.refresh();
};

function onPdfExportSelected(e) {
    // console.log('onPdfExportSelected', e);
}

const onGroupAreaClick = () => {
    groupPanelVisible.value = !groupPanelVisible.value;
};

const applyAutoSettings = () => {
    emit('save-settings');
};

const applyAutoSettingsSave = () => {
    refs.value['isSaveSettingsButtonDisabled'] = !vIsCurrentSettingsUser.value;
    vIsCurrentSettingsUser.value = !vIsCurrentSettingsUser.value;
};

const clearUserSettings = () => {
    emit('userSettingsReset');
};
const onEditingEnd = ({ rowIndex, columnIndex }) => {
    editCell(rowIndex, columnIndex);
};

const editCell = (rowIndex, columnIndex) => {
    setTimeout(async () => {
        let isNearestRowExist = !!(await gridInstance.value.getKeyByRowIndex(rowIndex + 1));
        let nearestRowIndex = rowIndex + 1;
        if (!isNearestRowExist) {
            if (rowIndex === 0) return;

            isNearestRowExist = !!(await gridInstance.value.getKeyByRowIndex(rowIndex - 1));
            nearestRowIndex = rowIndex - 1;

            if (!isNearestRowExist) return;
        }
        await gridInstance.value.editCell(nearestRowIndex, columnIndex);
        await gridInstance.value.editCell(rowIndex, columnIndex);
    }, 0);
};

const setSelectionMode = () => {
    if (props.meta.selectionMode === 'single') {
        selectionMode.value = 'none';
        return;
    }

    selectionMode.value = 'multiple';
};

const chooseEditor = ({ editor, editCellTemplate }) => {
    if (!editor?.type) return editCellTemplate;

    if (editor?.type === 'ai-drop-down') return 'dropDownBoxEditor';

    if (editor.type === 'ai-popup') return 'popupEditor';
};

const onLookUpSelect = event => {
    if (lookupAddData.value[event.keyExpr] === undefined) {
        lookupAddData.value[event.keyExpr] = [];
    }
    lookupAddData.value[event.keyExpr].push(event.row);
};

const lookupAddData = ref({});

const displayList = computed(() =>
    [...props.dataSource].map(item => ({ ...item, greyEminence: item.greyEminence ?? item[props.meta?.keyExpr] }))
);

const lookupList = computed(() => {
    const resObj = {};

    for (const dsKey of Object.keys(props.lookupsData)) {
        for (const dsRow of props.lookupsData[dsKey]) {
            if (resObj[dsKey] === undefined) {
                resObj[dsKey] = [];
            }

            const findRes = resObj[dsKey].find(item => {
                if (item[dsKey] === dsRow[dsKey]) {
                    return true;
                }
            });

            if (findRes === undefined) {
                resObj[dsKey].push(dsRow);
            }
        }
    }
    for (const dsKey of Object.keys(lookupAddData.value)) {
        for (const dsRow of lookupAddData.value[dsKey]) {
            if (resObj[dsKey] === undefined) {
                resObj[dsKey] = [];
            }
            const findRes = resObj[dsKey].find(item => {
                if (item[dsKey] === dsRow[dsKey]) {
                    return true;
                }
            });
            if (findRes === undefined) {
                resObj[dsKey].push(dsRow);
            }
        }
    }

    return { ...resObj };
});

const emit = defineEmits([
    'coreMounted',
    'selectClick',
    'saveClick',
    'deleteClick',
    'coreContextMenuPrepared',
    'userSettingsChanged',
    'userDataChanged',
    'userSettingsReset',
    'refreshClick',
    'focusChanged',
    'cellClick',
    'toolbarButtonClick',
    'save-settings',
    'changesRemoved',
    'onAddClickEvent',
]);

const selectionChanged = ({ selectedRowKeys, currentSelectedRowKeys }) => {
    if (props.lockedRows.includes(currentSelectedRowKeys[0])) {
        gridInstance.value.deselectRows(currentSelectedRowKeys);
        return;
    }

    const { selectButtonState, deleteButtonState } = toolbarState;

    childRef.value?.selectRows(selectedRowKeys.length);

    if (selectedRowKeys.length > 0) {
        if (deleteButtonState) {
            deleteButtonState.disabled = false;
            deleteButtonState.instance?.option('disabled', false);
        }

        if (selectButtonState) {
            selectButtonState.disabled = false;
            selectButtonState.instance?.option('disabled', false);
        }
        refs.value['isDeleteButtonDisabled'] = false;
        refs.value['isChoiceButtonDisabled'] = false;
    } else {
        if (deleteButtonState) {
            deleteButtonState.disabled = true;
            deleteButtonState.instance?.option('disabled', true);
        }

        if (selectButtonState) {
            selectButtonState.disabled = true;
            selectButtonState.instance?.option('disabled', true);
        }
        refs.value['isDeleteButtonDisabled'] = true;
        refs.value['isChoiceButtonDisabled'] = true;
    }
};

const saveState = state => {
    const { selectedRowKeys, columns } = state;

    const newUserSettings = { columns };
    const isSettingsHasChanged = !deepEqual(userSettingsData.value, newUserSettings);
    userSettingsData.value = newUserSettings;

    if (isSettingsHasChanged) {
        emit('userSettingsChanged', userSettingsData.value, vIsCurrentSettingsUser.value);
    }

    const newUserData = { ...userData.value, selectedRowKeys };
    const isUserDataHasChanged = !deepEqual(userData.value, newUserData);
    userData.value = newUserData;

    if (isUserDataHasChanged && !state.focusedRowKey) {
        emit('userDataChanged', userData.value, props.meta.viewId);
    }
};

const onRowClick = ({ key, event }) => {
    if (props.lockedRows.includes(key)) event.stopImmediatePropagation();
};

const onCellClick = e => {
    console.log('e.columnIndex', e.columnIndex);
    if (e.rowType === 'filter') focusedFilterColumnIndex.value = e.columnIndex;
    if (props.lockedRows.includes(e.key)) e.event.stopImmediatePropagation();

    emit('cellClick', props.meta.viewId, e.key);
};

// const onFocusedRowChanging = event => {
//     const { newRowIndex } = event;
//     const key = displayList.value[newRowIndex]?.greyEminence;
//
//     if (key && props.lockedRows.includes(key)) event.cancel = true;
// };
// let rowIndexes = [];
// let prevRowIndex;

const onFocusedRowChanged = async ({ rowIndex, row, component }) => {
    if (props.lockedRows.includes(row?.key)) return;

    const { copyButtonState, deleteButtonState, selectButtonState } = toolbarState;

    if (copyButtonState) {
        if (rowIndex !== -1) {
            copyButtonState.disabled = false;
            copyButtonState.instance?.option('disabled', false);
        } else {
            copyButtonState.disabled = true;
            copyButtonState.instance?.option('disabled', true);
        }
    }

    if (selectionMode.value === 'none') {
        if (rowIndex !== -1) {
            childRef.value.selectRows(1);
            if (deleteButtonState) {
                deleteButtonState.disabled = false;
                deleteButtonState.instance?.option('disabled', false);
            }

            if (selectButtonState) {
                selectButtonState.disabled = false;
                selectButtonState.instance?.option('disabled', false);
            }
        } else {
            childRef.value.selectRows(0);
            if (deleteButtonState) {
                deleteButtonState.disabled = true;
                deleteButtonState.instance?.option('disabled', true);
            }

            if (selectButtonState) {
                selectButtonState.disabled = true;
                selectButtonState.instance?.option('disabled', true);
            }
        }
    }
    refs.value['isCopyButtonDisabled'] = false;
    if (rowIndex !== 0 && rowIndex !== -1) {
        emit('focusChanged', row, props.meta.viewId, rowIndex, component);
    }
};

// function sortStringsConsideringCulture() {
//     console.log(this);
// }

// const onContentReady = () => {
//     totalCount.value = gridInstance.value.totalCount();

//     const { selectButtonState, deleteButtonState, copyButtonState, preferencesButtonState, saveChangesButtonState } =
//         toolbarState;

//     selectButtonState?.instance?.option('disabled', selectButtonState.disabled);
//     deleteButtonState?.instance?.option('disabled', deleteButtonState.disabled);
//     copyButtonState?.instance?.option('disabled', copyButtonState.disabled);
//     saveChangesButtonState?.instance?.option('disabled', saveChangesButtonState.disabled);
//     if (preferencesButtonState?.items) preferencesButtonState.instance?.option('items', preferencesButtonState.items);

//     //TODO: объединить в 1 функцию, которая получит все кнопки и проставит состояния

//     // setButtonsClasses();
// };

const onEditingStart = event => {
    if (props.lockedRows.includes(event.key)) {
        event.cancel = true;
    }
};

const onEditorPreparing = event => {
    const { parentType, editorOptions, dataField, row, index } = event;

    if (parentType === 'dataRow' && editorOptions) {
        const originalValue = getOriginalValue(row.key, dataField);

        editorOptions.onValueChanged = args => {
            if (event.row.isNewRow) {
                updateInsert(row.key, dataField, args.value);
                return;
            }
            updateChanges(row.key, dataField, args.value, originalValue);
        };
    }
};

const getOriginalValue = (key, dataField) => {
    const row = displayList.value.find(({ greyEminence }) => greyEminence === key);
    return row ? row[dataField] : null;
};

const updateInsert = (key, dataField, newValue) => {
    const changes = gridInstance.value.option('editing.changes');
    const changeIndex = changes.findIndex(change => change.key === key && change.type === 'insert');
    let trimmedValue = newValue;
    if (typeof newValue === 'string') {
        trimmedValue = newValue.trim() === '' ? null : newValue.trim();
    }

    if (changeIndex !== -1) {
        const updatedChangeData = { ...changes[changeIndex].data, [dataField]: trimmedValue };
        changes[changeIndex].data = updatedChangeData;

        gridInstance.value.option('editing.changes', [...changes]);
    }
};

const updateChanges = (key, dataField, newValue, originalValue) => {
    const changes = gridInstance.value.option('editing.changes');
    let trimmedValue = newValue;
    if (typeof newValue === 'string') {
        trimmedValue = newValue.trim() === '' ? null : newValue.trim();
    }

    changes.push({
        key: key,
        type: 'update',
        data: { [dataField]: trimmedValue },
    });

    const existingChange = changes.find(change => change.key === key && change.type === 'update');
    const condition =
        (String(trimmedValue) === String(originalValue) && existingChange) || (!trimmedValue && !originalValue);

    if (condition) {
        let properties = existingChange ? Object.keys(existingChange?.data) : null;
        properties = properties ? properties : [];
        const countOfProperties = properties.length;
        let updatedChanges = [];

        if (countOfProperties <= 1) {
            updatedChanges = changes.filter(change => change.key !== key);
        } else {
            updatedChanges = changes.map(change => {
                const condition = change.key === key && properties.includes(dataField);
                if (condition) {
                    const { [dataField]: _, ...rest } = change.data;
                    change.data = { ...rest };
                }
                return change;
            });
        }

        gridInstance.value.option('editing.changes', updatedChanges);
        return;
    }

    if (existingChange) {
        existingChange.data[dataField] = trimmedValue;
    }

    gridInstance.value.option('editing.changes', changes);
};

const onEditorPrepared = ({ parentType, editorElement, row, command, dataField }) => {
    if (parentType !== 'dataRow') return;

    const lockedCase = command === 'select' && row && props.lockedRows.includes(row.key);
    if (lockedCase) editorElement.classList.add('hidden');

    if (editorElement) {
        const originalValue = getOriginalValue(row.key, dataField);

        editorElement.addEventListener('input', ({ target }) => {
            const newValue = target.value;
            const cellClasses = editorElement.closest('td').classList;

            defferedUpdateUserData(row.key, dataField, newValue);

            const isCellValueModified = !(originalValue === newValue || (!originalValue && !newValue));

            if (isCellValueModified) cellClasses.add('dx-cell-modified');
            if (!isCellValueModified) cellClasses.remove('dx-cell-modified');
        });
    }
};

const defferedUpdateUserData = (key, dataField, value) => {
    let changes = [...userData.value.gridChanges];
    const existingChange = changes.find(change => change.key === key);

    if (existingChange) {
        if (existingChange.type === 'update') {
            existingChange.data[dataField] = value;
        }

        if (existingChange.type === 'insert') {
            existingChange.data = { ...existingChange.data, [dataField]: value };
        }

        changes = changes
            .map(change => {
                if (change.insertBeforeKey) {
                    const { insertBeforeKey, ...rest } = change;
                    return rest;
                }
                return change;
            })
            .filter(change => change.key !== existingChange.key);
        changes.push(existingChange);
    } else {
        changes.push({
            key: key,
            type: 'update',
            data: { [dataField]: value },
        });
    }

    // if (timeoutId.value) clearTimeout(timeoutId.value);

    userData.value = { ...userData.value, gridChanges: changes };
    emit('userDataChanged', userData.value);
};

function calculateCellValue(rowData) {
    if (this.dataType !== 'boolean') return rowData[this.dataField];

    if (!props.meta.enableThreeStateBehavior && rowData[this.dataField] === null) {
        return 0;
    }

    return rowData[this.dataField];
}

// watch(userData, () => {
//     emit('userDataChanged', userData.value, props.meta.viewId);
// });

const onOptionChanged = event => {
    if (event.name === 'columns') {
        const visibleColumns = gridInstance.value.getVisibleColumns();
        if (visibleColumns && visibleColumns.length > 0) {
            columnsMinWidth.value = 1000 / visibleColumns.length;
        }
    }
    if (event.fullName === 'editing.changes') {
        const lookupData = [];
        event.value.forEach(({ data, type }) => {
            if (type !== 'remove') {
                const keys = Object.keys(data);
                keys.forEach(key => {
                    if (Object.prototype.hasOwnProperty.call(lookupList.value, key)) {
                        const match = lookupList.value[key].find(obj => obj[key] === data[key]);
                        if (match) lookupData.push({ ...match });
                    }
                });
            }
        });

        const processedGridChanges = [...event.value].map(change => {
            if (change.type === 'insert') {
                const { insertBeforeKey, ...rest } = change;
                return rest;
            }
            return change;
        });

        userData.value = { ...userData.value, gridChanges: processedGridChanges, lookupData };

        const hasUndefinedFields = update => {
            return Object.keys(update.data).every(value => value === 'undefined');
        };

        const hasValidChanges = processedGridChanges.some(change => {
            if (change.type === 'remove') return true;
            return !hasUndefinedFields(change);
        });

        const { saveChangesButtonState } = toolbarState;
        const isSaveButtonDisabled = !hasValidChanges;

        if (refs.value?.isSaveButtonDisabled !== undefined) {
            refs.value.isSaveButtonDisabled = isSaveButtonDisabled;
        }

        if (saveChangesButtonState) {
            if (saveChangesButtonState.instance) {
                saveChangesButtonState.instance.option('disabled', isSaveButtonDisabled);
            }
            saveChangesButtonState.disabled = isSaveButtonDisabled;
        }

        if (event.previousValue.length && !event.previousValue.every(hasUndefinedFields)) {
            emit('userDataChanged', userData.value);
        }
    }
};

const setFocus = key => {
    setTimeout(() => {
        const index = gridInstance.value.getRowIndexByKey(key);
        const rowElement = gridInstance.value.getRowElement(index);

        gridInstance.value.option('focusedRowKey', key);
        gridInstance.value.option('focusedRowIndex', index);
        gridInstance.value.getScrollable().scrollToElement(rowElement);
    }, 500);
};

const onCopyClick = async () => {
    const key = gridInstance.value.option('focusedRowKey');
    const newRowData = await gridInstance.value.getDataByKeys([key]);

    gridInstance.value.getScrollable().scrollTo(0);

    setTimeout(async () => {
        const copy = {
            ...newRowData[0],
            isCopy: true,
            isNewRow: true,
            greyEminence: `${Math.random()}`,
        };

        const change = {
            type: 'insert',
            data: copy,
            key: copy.greyEminence,
            index: 0,
        };

        const changesOptions = [...gridInstance.value.option('editing.changes'), change];

        userData.value = { ...userData.value, gridChanges: changesOptions };
        emit('userDataChanged', userData.value);

        await gridInstance.value.option('editing.changes', changesOptions);
        await gridInstance.value.refresh();

        setFocus(copy.greyEminence);
    }, 500);
};

const onAddClick = async e => {
    if (props.addCustomClick) {
        gridInstance.value.getScrollable().scrollTo(0);

        emit('onAddClickEvent', e);
    } else {
        gridInstance.value.getScrollable().scrollTo(0);

        setTimeout(async () => {
            const blankRowData = visibleColumns.value.reduce((acc, key) => ({ ...acc, [key]: null }), {});
            const insert = { ...blankRowData, isNewRow: true, greyEminence: `${Math.random()}` };

            const change = {
                type: 'insert',
                data: insert,
                key: insert.greyEminence,
                index: 0,
            };

            const changesOptions = [...gridInstance.value.option('editing.changes'), change];
            userData.value = { ...userData.value, gridChanges: changesOptions };
            emit('userDataChanged', userData.value);

            await gridInstance.value.option('editing.changes', changesOptions);
            await gridInstance.value.refresh();

            setFocus(insert.greyEminence);
        }, 200);
    }
};

const onDeleteClick = () => {
    let selectedRows = [];

    if (selectionMode.value === 'none') {
        selectedRows = [gridInstance.value.option('focusedRowKey')];
    } else {
        selectedRows = gridInstance.value.getSelectedRowsData();
    }

    processDeleting(selectedRows);
};

const processDeleting = rowsToDelete => {
    if (rowsToDelete.length === 1 && rowsToDelete[0].isNewRow && !props.meta.cancelDeleteOneRow) {
        gridInstance.value.deleteRow(rowsToDelete[0].rowIndexFront);
        return;
    }

    const processedData = {
        insertDataArgs: [],
        updateDataArgs: [],
        deleteDataArgs: rowsToDelete,
    };

    emit('deleteClick', processedData);
};

const onSaveClick = async event => {
    event.cancel = true;

    const processedData = await prepareChangesForSaving();
    emit('saveClick', processedData, event, props.meta);
};

const prepareChangesForSaving = async () => {
    const processedData = {
        insertDataArgs: [],
        updateDataArgs: [],
        deleteDataArgs: [],
    };

    for (const { type, data, key } of userData.value.gridChanges) {
        let processedRowData;

        if (type === 'insert') {
            processedRowData = { ...data };
        } else {
            const fullRowFromDs = displayList.value.find(row => row.greyEminence === key);
            processedRowData = { ...fullRowFromDs, ...data };
        }

        Object.keys(processedRowData).forEach(key => {
            if (booleanColumns.value.includes(key)) processedRowData[key] = processedRowData[key] ? 1 : 0;
            if (processedRowData[key] === '') processedRowData[key] = null;
        });

        switch (type) {
            case 'remove': {
                processedData.deleteDataArgs.push({ ...processedRowData });
                break;
            }
            case 'update': {
                if (processedRowData.isNewRow) {
                    processedData.insertDataArgs.push(processedRowData);
                    break;
                }

                processedData.updateDataArgs.push({ ...processedRowData });
                break;
            }
            case 'insert': {
                const blankRow = visibleColumns.value.reduce((acc, key) => ({ ...acc, [key]: null }), {});
                const merge = { ...blankRow, ...processedRowData };
                processedData.insertDataArgs.push(merge);
                break;
            }
            default:
                break;
        }
    }

    return processedData;
};

const onContextMenuPreparing = event => {
    if (event.target !== 'content') return;
    if (!event.row) return;
    if (props.lockedRows.includes(event.row.key)) return;

    if (!event.items) event.items = [];
    if (props.meta?.contextMenuItems)
        event.items.push(
            ...props.meta.contextMenuItems.map(item => ({
                ...item,
                onItemClick: () => item.onItemClick(event),
            }))
        );
    if (props.editing?.allowAdding) event.items.push({ text: 'Копировать', onItemClick: onCopyClick });
    if ((props.editing?.enableDeleting && event.row) || event.row?.data?.isCopy || event.row?.isNewRow) {
        const rowData = { ...event.row.data, rowIndexFront: event.rowIndex };

        event.items.push({ text: 'Удалить', onItemClick: () => processDeleting([rowData]) });
    }

    emit('coreContextMenuPrepared', event);
};

const onRefreshClick = async () => {
    const processedData = await prepareChangesForSaving();
    emit('refreshClick', userData.value.gridChanges, processedData, props.meta);
};

const preferencesCheckChanged = ({ value }) => {
    const newItems = [];
    const { preferencesButtonState } = toolbarState;
    const disableValue = !!value;

    preferencesButtonState.instance?.option('items').forEach(item => {
        if (item.id === 2) item.disabled = disableValue;
        newItems.push(item);
    });

    userSettingsData.value = { ...userSettingsData.value, preferencesState: disableValue };
    preferencesButtonState.items = newItems;
    preferencesButtonState.instance?.option('items', newItems);
    if (vIsCurrentSettingsUser.value) {
        emit('userSettingsChanged', userSettingsData.value, props.meta);
    }
};

const delay = ms => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

const getGridDataSource = async (ms = 500) => {
    await delay(ms);
    return displayList.value;
};

onUnmounted(() => {
    const gridElement = gridInstance.value.element();
    const filterInputs = gridElement.querySelectorAll('.dx-datagrid-filter-row .dx-texteditor-input');
    filterInputs.forEach(input => {
        input.removeEventListener('keydown', input._enterHandler);
    });
});

defineExpose({
    gridInstance,
    getGridDataSource,
    userData,
    userSettingsData,
    refreshClick: onRefreshClick,
    prepareChangesForSaving,
    getRefValue,
    setRefValue,
    lookupAddData,
    getButtonsRefs: async () => {
        await delay(100);
        return refs;
    },
    onAddClick,
});
</script>

<style lang="scss" scoped>
:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}

:deep(.line-clamp-3) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
}

:deep(.dx-datagrid .dx-column-indicators .dx-sort-index-icon) {
    display: none;
}

// :deep(.dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row > td) {
//     vertical-align: text-top !important;
// }

.grid-border {
    border: 1px solid #e5e7eb;
}

:deep(td.dx-cell-focus-disabled.dx-master-detail-cell) {
    background: #f6fafe !important;
    padding: 30px;
}

.custom-toolbar {
    :deep(.dx-datagrid-header-panel) {
        min-width: fit-content;
        width: 100%;
        display: flex;
    }

    :deep(.dx-toolbar-center:empty) {
        display: flex;
    }
}

:deep(.dx-clear-button-area) {
    margin-left: 8px !important; /* Внешний отступ от поля ввода */
}
</style>
